import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";
import type { ReportNoteTabKey } from "../../constants/reportNotes";
import { createNote, deleteNote, listNotes, NotesApiError, patchNote } from "../../services/notesApi";
import type { Note, NoteEntityType } from "../../types/notes";
import { toastError, presentMutationError } from "../../utils/showApiErrorToast";

export type NotesListScope =
  | { entityType: "backtest"; entityId: string }
  | { entityType: "broker_account"; entityId: string }
  | { entityType: "report"; entityId: string; scope: "master" }
  | { entityType: "report"; entityId: string; tabKey: ReportNoteTabKey };

function notesQueryFromScope(scope: NotesListScope) {
  if (scope.entityType === "backtest") {
    return { entity_type: "backtest" as const, entity_id: scope.entityId };
  }
  if (scope.entityType === "broker_account") {
    return { entity_type: "broker_account" as const, entity_id: scope.entityId };
  }
  if ("tabKey" in scope) {
    return { entity_type: "report" as const, entity_id: scope.entityId, tab_key: scope.tabKey };
  }
  return { entity_type: "report" as const, entity_id: scope.entityId, scope: "master" as const };
}

function createBodyFromScope(scope: NotesListScope, body: string) {
  if (scope.entityType === "backtest") {
    return { entity_type: "backtest" as const, entity_id: scope.entityId, body };
  }
  if (scope.entityType === "broker_account") {
    return { entity_type: "broker_account" as const, entity_id: scope.entityId, body };
  }
  if ("tabKey" in scope) {
    return { entity_type: "report" as const, entity_id: scope.entityId, body, tab_key: scope.tabKey };
  }
  return { entity_type: "report" as const, entity_id: scope.entityId, body, scope: "master" as const };
}

export function useNotesList(scope: NotesListScope) {
  const entityType = scope.entityType;
  const entityId = scope.entityId;
  const tabKey = scope.entityType === "report" && "tabKey" in scope ? scope.tabKey : undefined;
  const isMasterScope = scope.entityType === "report" && "scope" in scope;
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Note[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [saving, setSaving] = useState(false);

  const canSubmit = draft.trim().length > 0 && !saving;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query =
        entityType === "backtest"
          ? { entity_type: "backtest" as const, entity_id: entityId }
          : entityType === "broker_account"
            ? { entity_type: "broker_account" as const, entity_id: entityId }
            : tabKey
              ? { entity_type: "report" as const, entity_id: entityId, tab_key: tabKey }
              : { entity_type: "report" as const, entity_id: entityId, scope: "master" as const };
      const resp = await listNotes(query);
      setNotes(resp.notes);
    } catch (e) {
      const message =
        e instanceof NotesApiError
          ? e.detail || e.message
          : e instanceof Error
            ? e.message
            : "Failed to load notes.";
      setError(message);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, tabKey, isMasterScope]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const startEdit = useCallback((note: Note) => {
    setEditingId(note.id);
    setEditingBody(note.body || "");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingBody("");
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const updated = await patchNote(editingId, { body: editingBody });
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      toast.success("Note updated");
      cancelEdit();
    } catch (e) {
      presentMutationError(e, "Update failed");
    } finally {
      setSaving(false);
    }
  }, [cancelEdit, editingBody, editingId]);

  const onCreate = useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const createBody =
        entityType === "backtest"
          ? { entity_type: "backtest" as const, entity_id: entityId, body: draft.trim() }
          : entityType === "broker_account"
            ? {
                entity_type: "broker_account" as const,
                entity_id: entityId,
                body: draft.trim(),
              }
            : tabKey
              ? {
                  entity_type: "report" as const,
                  entity_id: entityId,
                  body: draft.trim(),
                  tab_key: tabKey,
                }
              : {
                  entity_type: "report" as const,
                  entity_id: entityId,
                  body: draft.trim(),
                  scope: "master" as const,
                };
      const created = await createNote(createBody);
      setDraft("");
      setNotes((prev) => [created, ...prev]);
      toast.success("Note added");
    } catch (e) {
      toastError("Create failed", {
        description:
          e instanceof NotesApiError
            ? e.detail || e.message
            : e instanceof Error
              ? e.message
              : "Failed to create note.",
      });
    } finally {
      setSaving(false);
    }
  }, [canSubmit, draft, entityType, entityId, tabKey]);

  const onDelete = useCallback(
    async (noteId: string): Promise<boolean> => {
      if (!noteId) return false;
      setSaving(true);
      try {
        await deleteNote(noteId);
        setNotes((prev) => prev.filter((n) => n.id !== noteId));
        if (editingId === noteId) cancelEdit();
        toast.info("Note deleted");
        return true;
      } catch (e) {
        presentMutationError(e, "Delete failed");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [cancelEdit, editingId]
  );

  return {
    loading,
    notes,
    error,
    draft,
    setDraft,
    editingId,
    editingBody,
    setEditingBody,
    saving,
    canSubmit,
    reload,
    startEdit,
    cancelEdit,
    saveEdit,
    onCreate,
    onDelete,
  };
}

/** Replace notes in external grouped state after CRUD (hub panel). */
export function useNotesListControlled(
  notes: Note[],
  setNotes: Dispatch<SetStateAction<Note[]>>,
  scope: NotesListScope
) {
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [saving, setSaving] = useState(false);
  const canSubmit = draft.trim().length > 0 && !saving;

  const startEdit = useCallback((note: Note) => {
    setEditingId(note.id);
    setEditingBody(note.body || "");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingBody("");
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const updated = await patchNote(editingId, { body: editingBody });
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      toast.success("Note updated");
      cancelEdit();
    } catch (e) {
      presentMutationError(e, "Update failed");
    } finally {
      setSaving(false);
    }
  }, [cancelEdit, editingBody, editingId, setNotes]);

  const onCreate = useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const created = await createNote(createBodyFromScope(scope, draft.trim()));
      setDraft("");
      setNotes((prev) => [created, ...prev]);
      toast.success("Note added");
    } catch (e) {
      toastError("Create failed", {
        description:
          e instanceof NotesApiError
            ? e.detail || e.message
            : e instanceof Error
              ? e.message
              : "Failed to create note.",
      });
    } finally {
      setSaving(false);
    }
  }, [canSubmit, draft, scope, setNotes]);

  const onDelete = useCallback(
    async (noteId: string): Promise<boolean> => {
      if (!noteId) return false;
      setSaving(true);
      try {
        await deleteNote(noteId);
        setNotes((prev) => prev.filter((n) => n.id !== noteId));
        if (editingId === noteId) cancelEdit();
        toast.info("Note deleted");
        return true;
      } catch (e) {
        presentMutationError(e, "Delete failed");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [cancelEdit, editingId, setNotes]
  );

  return {
    notes,
    draft,
    setDraft,
    editingId,
    editingBody,
    setEditingBody,
    saving,
    canSubmit,
    startEdit,
    cancelEdit,
    saveEdit,
    onCreate,
    onDelete,
  };
}
